import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { resolveFishipediaEntry } from "../../../utils/fishipediaResolver";
import { resolveFishModalContent } from "../../../utils/fishDetailsResolver";
import "../../../styles/components/WeatherOverlay.css";

function reorderFishName(input) {
  const text =
    typeof input === "object" && input !== null ? input.name || input.displayName : input;

  if (!text) {
    return "";
  }

  const match = text.match(/^([A-Z][a-z]+ [a-z]+)\s*\(([^)]+)\)$/);
  return match ? `${match[2]} (${match[1]})` : text;
}

export default function FishInfoModal({
  fishName,
  onClose,
  onNavigateAway,
  mapState,
}) {
  const { t } = useTranslation();
  const [fishipediaNotice, setFishipediaNotice] = useState(null);
  const [modalContent, setModalContent] = useState({
    loaded: false,
    detail: null,
    imageSrc: null,
    fishipediaEntry: null,
  });
  const defaultNoticeHideMs = 3200;
  const moreNoticeHideMs = defaultNoticeHideMs * 2;

  useEffect(() => {
    if (!fishipediaNotice) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setFishipediaNotice(null);
    }, fishipediaNotice.hideAfterMs || defaultNoticeHideMs);

    return () => window.clearTimeout(timer);
  }, [defaultNoticeHideMs, fishipediaNotice]);

  useEffect(() => {
    let cancelled = false;

    if (!fishName) {
      setModalContent({
        loaded: true,
        detail: null,
        imageSrc: null,
        fishipediaEntry: null,
      });
      return undefined;
    }

    setModalContent({
      loaded: false,
      detail: null,
      imageSrc: null,
      fishipediaEntry: null,
    });

    async function loadModalContent() {
      try {
        const resolvedContent = await resolveFishModalContent(fishName, {
          waterType: mapState?.environment || null,
        });

        if (!cancelled) {
          setModalContent({
            loaded: true,
            detail: resolvedContent?.detail ?? null,
            imageSrc: resolvedContent?.imageSrc ?? null,
            fishipediaEntry: resolvedContent?.fishipediaEntry ?? null,
          });
        }
      } catch {
        if (!cancelled) {
          setModalContent({
            loaded: true,
            detail: null,
            imageSrc: null,
            fishipediaEntry: null,
          });
        }
      }
    }

    loadModalContent();

    return () => {
      cancelled = true;
    };
  }, [fishName, mapState?.environment]);

  if (!fishName) {
    return null;
  }

  const isObject = typeof fishName === "object" && fishName !== null;
  const nameString = isObject ? fishName.name : fishName;
  const detail = modalContent.detail;
  const titleText = reorderFishName(detail?.displayName || nameString);
  const fishipediaEntry =
    modalContent.fishipediaEntry || resolveFishipediaEntry(detail || fishName);

  return (
    <div className="fish-modal-overlay" onClick={onClose}>
      <div className="fish-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="fish-modal-header">
          <h3>{titleText}</h3>
          <button
            type="button"
            className="fish-modal-close map-overlay-close-button"
            onClick={onClose}
            aria-label={t("common_close")}
          >
            &times;
          </button>
        </div>

        {modalContent.imageSrc ? (
          <img
            src={modalContent.imageSrc}
            alt={nameString}
            className="fish-modal-image"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        ) : null}

        {detail ? (
          <>
            <p>{detail.description}</p>
            <p>
              <b>{t("fish_labels_habitat")}:</b> {detail.habitat}
            </p>
            <p>
              <b>{t("fish_labels_bait")}:</b> {detail.bait}
            </p>
            <p>
              <b>{t("fish_labels_season")}:</b> {detail.season}
            </p>
          </>
        ) : modalContent.loaded ? (
          <div style={{ padding: "15px 0", fontStyle: "italic", opacity: 0.8 }}>
            <p>
              {t("fish.view_details_online", {
                defaultValue: "View detailed species info on Fishipedia.",
              })}
            </p>
          </div>
        ) : null}

        <div className="fish-modal-actions">
          <button
            className="fish-modal-button fish-modal-button-secondary"
            onClick={() =>
              setFishipediaNotice({
                id: Date.now(),
                hideAfterMs: moreNoticeHideMs,
                message: t("fish_more_in_development", {
                  defaultValue:
                    "This feature is still in development. Soon you will be able to view more location-specific information about the selected fish species here, including the best times and seasons, suitable lures, and other practical details. For general species information, please use 'Read more in Fishipedia'.",
                }),
              })
            }
          >
            {t("common_more")}
          </button>

          <button
            className="fish-modal-button fish-modal-button-primary"
            onClick={() => {
              if (!fishipediaEntry?.slug) {
                setFishipediaNotice({
                  id: Date.now(),
                  message: t("fish_fishipedia_unavailable", {
                    defaultValue:
                      "This species is not yet available in Fishipedia. We update the library regularly and it will be added soon.",
                  }),
                });
                return;
              }

              window.dispatchEvent(
                new CustomEvent("saveMapStateNow", {
                  detail: {
                    overlay: {
                      isOpen: true,
                      tab: "fish",
                    },
                  },
                })
              );

              onNavigateAway?.({
                slug: fishipediaEntry.slug,
                fishName: nameString,
                mapState,
              });
            }}
          >
            {t("fish.read_more", { defaultValue: "Read more in Fishipedia" })}
          </button>
        </div>

        {fishipediaNotice ? (
          <div key={fishipediaNotice.id} className="fish-modal-notice">
            {fishipediaNotice.message}
          </div>
        ) : null}
      </div>
    </div>
  );
}

